import React, { useEffect } from 'react';
import { Box, Button, ButtonBase, Chip, CircularProgress, Collapse, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ACTIVITY_STATUSES, STATUS_BADGE, normalizeStatus, statusLabel } from '../../pages/impacts/activityConstants';
import { LEVEL_BADGE, normalizeLevel } from '../../pages/impacts/impactConstants';

// ── "Impacts & activities" ───────────────────────────────────────────────────
// The By-group single view's read-only look at the Register for ONE selection:
// its impacts, and the activities under each with their status. Collapsed by
// default behind a header that already carries the roll-up, so the chart above
// stays the first thing read.
//
// Fetched on selection rather than on expand: it is a plain read (no model),
// and the collapsed header is only worth having if it can say how much is
// behind it. The state lives in the parent, keyed by `reportId:scope_key`:
//   entry     undefined | { loading } | { error: { status } } | { data }
//
// Statuses and levels paint with the Activity Dashboard's and the Register's
// own palettes, so a chip means the same thing on every screen.

const INK = '#16335c';
const MUTED = '#6b7a90';
const FAINT = '#8493a6';
const ACCENT = '#1565c0';
const LINE = '#e6ebf2';
const DANGER = '#c53434';
const NUMERIC = { fontVariantNumeric: 'tabular-nums' };
const EMPTY_STATUSES = ['no_impacts', 'no_groups'];

const statusOrder = (activity) => ACTIVITY_STATUSES.indexOf(normalizeStatus(activity.status));

function StatusChip({ status, count }) {
    const intl = useIntl();
    const key = normalizeStatus(status);
    const badge = STATUS_BADGE[key];
    const label = statusLabel(intl, key);
    return (
        <Chip
            size="small"
            data-testid={`group-impacts-status-${key}`}
            label={count == null ? label : `${label} ${intl.formatNumber(count)}`}
            sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: badge.bg, color: badge.color, ...NUMERIC }}
        />
    );
}

function LevelChip({ level }) {
    const intl = useIntl();
    const key = normalizeLevel(level);
    const badge = LEVEL_BADGE[key];
    if (!badge) return null;
    const id = `impact.level_${key}`;
    return (
        <Chip
            size="small"
            data-testid="group-impact-level"
            label={intl.messages?.[id] ? intl.formatMessage({ id }) : badge.short}
            sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: badge.bg, color: badge.color }}
        />
    );
}

function ImpactRow({ impact }) {
    const activities = [...(impact.activities || [])].sort((a, b) => statusOrder(a) - statusOrder(b) || a.id - b.id);
    const where = [impact.business_area, impact.group_name].filter(Boolean).join(' · ');
    const done = impact.activity_counts?.done ?? activities.filter((a) => normalizeStatus(a.status) === 'done').length;
    return (
        <Box
            component="li"
            data-testid="group-impact-row"
            sx={{ border: `1px solid ${LINE}`, borderRadius: 2.5, bgcolor: '#fff', px: { xs: 1.5, sm: 2 }, py: 1.5 }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography data-testid="group-impact-title" sx={{ fontSize: 14.5, fontWeight: 700, color: INK, lineHeight: 1.4 }}>
                        {impact.title}
                    </Typography>
                    {where && (
                        <Typography data-testid="group-impact-where" variant="caption" sx={{ color: MUTED }}>
                            {where}
                        </Typography>
                    )}
                </Box>
                <LevelChip level={impact.level} />
                {activities.length > 0 && (
                    <Typography variant="caption" sx={{ color: FAINT, fontWeight: 600, ...NUMERIC, mt: '2px' }}>
                        <FormattedMessage
                            id="groupImpacts.done_of"
                            defaultMessage="{done}/{total} done"
                            values={{ done, total: activities.length }}
                        />
                    </Typography>
                )}
            </Box>
            {activities.length === 0 ? (
                <Typography data-testid="group-impact-no-activities" variant="body2" sx={{ color: FAINT, mt: 1, fontSize: 13 }}>
                    <FormattedMessage id="groupImpacts.no_activities" defaultMessage="No activities yet." />
                </Typography>
            ) : (
                <Box component="ul" sx={{ m: 0, mt: 1, p: 0, listStyle: 'none', borderTop: `1px solid ${LINE}` }}>
                    {activities.map((activity) => (
                        <Box
                            component="li"
                            key={activity.id}
                            data-testid="group-impact-activity"
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.9, '& + li': { borderTop: '1px dashed #eef1f6' } }}
                        >
                            <StatusChip status={activity.status} />
                            <Typography variant="body2" sx={{ flex: 1, minWidth: 140, color: '#33445c', fontSize: 13.5 }}>
                                {activity.title}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default function GroupImpactsPanel({ reportId, scopeKey, entry, onLoad, open, onToggle }) {
    const data = entry?.data;
    const ready = data?.status === 'ready';

    useEffect(() => {
        if (!entry) onLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportId, scopeKey, entry]);

    // Nothing in the Register for this selection: "What to do next" already
    // says to fill in the assessment, so this stays out of the way. A selection
    // the BE does not serve (404) has nothing to show either.
    if (EMPTY_STATUSES.includes(data?.status) || entry?.error?.status === 404) return null;
    if (data && (!ready || !(data.impacts || []).length)) return null;

    const totals = data?.totals || {};
    const byStatus = totals.by_status || {};
    const loading = !entry || entry.loading;

    return (
        <Box
            data-testid="group-impacts-panel"
            sx={{ mt: 2, borderRadius: 3, border: `1px solid ${LINE}`, bgcolor: '#fbfcfe', overflow: 'hidden' }}
        >
            <ButtonBase
                data-testid="group-impacts-toggle"
                aria-expanded={open}
                disabled={!ready}
                onClick={onToggle}
                sx={{
                    width: '100%', justifyContent: 'flex-start', textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                    px: { xs: 2, sm: 2.75 }, py: 1.5,
                    '&:hover': { bgcolor: ready ? '#f3f6fb' : 'transparent' },
                }}
            >
                <AssignmentOutlinedIcon fontSize="small" sx={{ color: ACCENT }} />
                <Typography component="h3" data-testid="group-impacts-title" sx={{ fontSize: 15, fontWeight: 700, color: INK }}>
                    <FormattedMessage id="groupImpacts.title" defaultMessage="Impacts & activities" />
                    {ready && (
                        <Box component="span" sx={{ fontWeight: 500, color: MUTED, ml: 0.75, ...NUMERIC }}>
                            <FormattedMessage
                                id="groupImpacts.counts"
                                defaultMessage="({impacts, plural, one {# impact} other {# impacts}} · {activities, plural, one {# activity} other {# activities}})"
                                values={{ impacts: totals.impacts ?? 0, activities: totals.activities ?? 0 }}
                            />
                        </Box>
                    )}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {loading && <CircularProgress size={14} data-testid="group-impacts-loading" />}
                {ready && (
                    <Box data-testid="group-impacts-rollup" sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {[...ACTIVITY_STATUSES].reverse()
                            .filter((status) => byStatus[status] > 0)
                            .map((status) => <StatusChip key={status} status={status} count={byStatus[status]} />)}
                    </Box>
                )}
                {ready && (
                    <ExpandMoreIcon
                        fontSize="small"
                        sx={{ color: MUTED, transition: 'transform 150ms', transform: open ? 'rotate(180deg)' : 'none' }}
                    />
                )}
            </ButtonBase>
            {entry?.error && (
                <Box data-testid="group-impacts-error" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: { xs: 2, sm: 2.75 }, pb: 1.5 }}>
                    <Typography variant="body2" sx={{ color: DANGER }}>
                        <FormattedMessage id="groupImpacts.failed" defaultMessage="Couldn't load the impacts." />
                    </Typography>
                    <Button data-testid="group-impacts-retry" size="small" onClick={onLoad} sx={{ textTransform: 'none', fontWeight: 600 }}>
                        <FormattedMessage id="groupImpacts.retry" defaultMessage="Retry" />
                    </Button>
                </Box>
            )}
            {ready && (
                <Collapse in={open} unmountOnExit>
                    <Box
                        component="ol"
                        data-testid="group-impacts-list"
                        sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1.25, px: { xs: 1.5, sm: 2.25 }, pb: 2, pt: 0.5 }}
                    >
                        {data.impacts.map((impact) => (
                            <ImpactRow key={impact.id} impact={impact} />
                        ))}
                    </Box>
                </Collapse>
            )}
        </Box>
    );
}
